import * as React from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "@/store/store";
import { cn } from "@/lib/utils";
import { ToggleGroup, ToggleGroupItem } from "@radix-ui/react-toggle-group";
import { format } from "date-fns";
import searchIcon from "@/assets/svg/searchIcon.svg";
import searchIconLight from "@/assets/svg/searchIconLight.svg";
import {
  validateDOBLastName,
  validateMedicaidID,
  formatDateWithoutTimezone,
} from "@/constants/utilityFunctions";
import { MESSAGES } from "@/constants/messages";
import { Typography } from "@/components/ui/typography";
import { DatePicker } from "@/components/ui/date-picker";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Patient,
  setSelectedPatient,
  SearchError,
} from "@/store/slices/searchSlice";
import api from "@/axios";
import { routes } from "@/utils/urls";

interface SearchToggleOption {
  value: string;
  label: string;
}

interface SearchInputProps {
  error?: string;
  icon?: React.ReactNode;
  onSearchChange?: (value: string) => void;
  toggleOptions?: SearchToggleOption[];
  onToggleChange?: (value: string) => void;
  onSuggestionSelect?: (patient: Patient) => void;
  defaultToggle?: string;
  placeholder?: string;
  suggestions?: string[];
}

const SearchWithToggle = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({
    error,
    icon,
    onSearchChange,
    toggleOptions = [
      { value: "dob-name", label: "DOB + Last Name" },
      { value: "medicaid-id", label: "Medicaid ID" },
    ],
    onToggleChange,
    onSuggestionSelect,
    defaultToggle = "dob-name",
    placeholder = "Search by Patient's Medicaid ID (e.g. NCXXXXXXXXX)",
    suggestions: injectedSuggestions,
  }) => { // NOSONAR
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();

    // Local state for search results (isolated per component instance)
    const [localResults, setLocalResults] = React.useState<Patient[]>([]);
    const [localLoading, setLocalLoading] = React.useState(false);
    const [localSearchError, setLocalSearchError] =
      React.useState<SearchError | null>(null);
    const [localLastMessage, setLocalLastMessage] = React.useState<
      string | undefined
    >(undefined);

    const normalizeSearchType = (value: string): "medicaid-id" | "dob-name" => {
      const v = (value || "").toLowerCase();
      if (v.includes("medicaid")) return "medicaid-id";
      if (
        v.includes("dob") ||
        v.includes("name") ||
        v.includes("lastname") ||
        v.includes("last-name")
      )
        return "dob-name";
      return v === "medicaid-id" ? "medicaid-id" : "dob-name";
    };

    const [isFocused, setIsFocused] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState("");
    const [activeToggle, setActiveToggle] = React.useState(defaultToggle);
    const [debounceTimer, setDebounceTimer] =
      React.useState<NodeJS.Timeout | null>(null);
    const [validationTimer, setValidationTimer] =
      React.useState<NodeJS.Timeout | null>(null);
    const [validationError, setValidationError] = React.useState<string | null>(
      null
    );
    const [selectedIndex, setSelectedIndex] = React.useState<number>(-1);
    const [dobValue, setDobValue] = React.useState<Date | undefined>(undefined);
    const [lastNameValue, setLastNameValue] = React.useState("");
    const lastNameInputRef = React.useRef<HTMLInputElement>(null);
    const medicaidInputRef = React.useRef<HTMLInputElement>(null);
    const [shouldFocusDatePicker, setShouldFocusDatePicker] =
      React.useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = React.useState(false);
    const [dobError, setDobError] = React.useState<string | null>(null);
    const prevPathnameRef = React.useRef<string>("");
    const suppressFocusOnCloseRef = React.useRef(false);
    const containerRef = React.useRef<HTMLDivElement | null>(null);

    // Check if any error exists (excluding localSearchError as it shows in dropdown)
    const hasError = !!(validationError || error);

    // Determine the active suggestions list
    const activeSuggestions =
      injectedSuggestions && injectedSuggestions.length > 0
        ? injectedSuggestions
        : localResults;

    // Local search function
    const performSearch = async (
      query: string,
      searchType: "medicaid-id" | "dob-name"
    ) => {
      setLocalLoading(true);
      setLocalSearchError(null);
      setLocalLastMessage(undefined);

      try {
        const apiResponse = await api.post(routes.search.patients, {
          query: query.trim(),
        });
        const data = apiResponse.data;

        if (Array.isArray(data?.data)) {
          setLocalResults(data.data);
          setLocalLastMessage(data.message);
        } else {
          setLocalResults([]);
          const message =
            searchType === "dob-name"
              ? "No patient(s) found matching the search criteria"
              : "No patient found matching the search criteria";
          setLocalLastMessage(message);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        console.error("Search error:", error);

        setLocalResults([]);
        const message =
          searchType === "dob-name"
            ? "No patient(s) found matching the search criteria"
            : "No patient found matching the search criteria";
        setLocalLastMessage(message);
      } finally {
        setLocalLoading(false);
      }
    };

    // Clear local search state
    const clearLocalSearch = () => {
      setLocalResults([]);
      setLocalSearchError(null);
      setLocalLastMessage(undefined);
    };

    // Clear all search state (used when navigation happens)
    const clearAllSearchState = React.useCallback(() => {
      setSearchValue("");
      setDobValue(undefined);
      setLastNameValue("");
      setValidationError(null);
      setDobError(null);
      setSelectedIndex(-1);
      setIsFocused(false);
      clearLocalSearch();

      // Clear timers using functional updates
      setDebounceTimer((prev) => {
        if (prev) {
          clearTimeout(prev);
        }
        return null;
      });
      setValidationTimer((prev) => {
        if (prev) {
          clearTimeout(prev);
        }
        return null;
      });
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }, []);

    const handleSearchValueChange = (val: string) => {
      setSearchValue(val);
      setSelectedIndex(-1);
      onSearchChange?.(val);

      // Only clear validationError here, not dobError (dobError is managed by DatePicker)
      setValidationError(null);

      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      if (validationTimer) {
        clearTimeout(validationTimer);
      }

      const trimmedVal = val.trim();

      if (trimmedVal.length === 0) {
        clearLocalSearch();
        setDobError(null);
        return;
      }

      // ADD THIS: Clear local results immediately when input changes
      // This prevents old results from showing while validation is in progress
      clearLocalSearch();

      if (trimmedVal.length >= 3) {
        const timer = setTimeout(() => {
          const searchType = normalizeSearchType(activeToggle);
          if (searchType === "dob-name") {
            const hasDatePattern = /\d{1,2}\/\d{1,2}\/\d{4}/.test(trimmedVal);
            if (!hasDatePattern && !dobValue) {
              setDobError(null);
              return;
            }
          }

          const validation =
            searchType === "medicaid-id"
              ? validateMedicaidID(trimmedVal)
              : validateDOBLastName(val);

          if (!validation.isValid) {
            const vt = setTimeout(() => {
              const errorMsg = validation.errorMessage || null;

              if (searchType === "dob-name" && errorMsg) {
                // If DOB already has an error (e.g., future date), prefer showing DOB error
                if (dobError && errorMsg === MESSAGES.first3LettersAfterDob) {
                  // Don't overwrite DOB error with a last-name prompt
                  return;
                }

                // DOB-related errors should appear under the DOB field
                if (
                  errorMsg === MESSAGES.enterCompleteDob ||
                  errorMsg === MESSAGES.dobFormat ||
                  errorMsg === MESSAGES.futureDate
                ) {
                  setDobError(errorMsg);
                  setValidationError(null);
                }
                // Last-name related errors
                else if (
                  errorMsg === MESSAGES.lastNameMin3 ||
                  errorMsg === MESSAGES.first3LettersAfterDob ||
                  errorMsg === MESSAGES.lastNameLettersOnly
                ) {
                  setValidationError(errorMsg);
                } else {
                  // Fallback: show any other errors in validationError
                  setValidationError(errorMsg);
                }
              } else {
                // Medicaid ID errors go to validationError
                setValidationError(errorMsg);
              }
            }, 500);
            setValidationTimer(vt);
            return;
          }

          // Clear errors only if validation passed
          const vtClear = setTimeout(() => {
            setValidationError(null);
            setDobError(null);
          }, 500);
          setValidationTimer(vtClear);

          // Use local search instead of Redux
          performSearch(trimmedVal, searchType);
        }, 500);
        setDebounceTimer(timer);
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleSearchValueChange(e.target.value);
    };

    const formatDobForSearch = (value?: Date) => {
      if (!value) return "";
      return format(value, "MM/dd/yyyy");
    };

    const triggerDobSearch = (nextLastName: string, nextDob?: Date) => {
      const formattedDob = formatDobForSearch(nextDob);
      const combined = [formattedDob, nextLastName.trim()]
        .filter(Boolean)
        .join(" ")
        .trim();
      handleSearchValueChange(combined);
    };

    const handleDobDateChange = (value?: Date) => {
      setDobValue(value);
      setIsFocused(true);
      setIsDatePickerOpen(false);

      // Clear any pending timers
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        setDebounceTimer(null);
      }

      // If we have a date and last name is less than 3 characters, show error
      if (value && lastNameValue.trim().length < 3) {
        setValidationError(MESSAGES.first3LettersAfterDob);
        return;
      }

      setValidationError(null);

      // Trigger search immediately if we have both DOB and 3-character last name
      if (value && lastNameValue.length === 3) {
        const formattedDob = formatDobForSearch(value);
        const combined = [formattedDob, lastNameValue.trim()]
          .filter(Boolean)
          .join(" ")
          .trim();

        handleSearchValueChange(combined);

        setTimeout(() => {
          setIsFocused(true);
        }, 50);
      }
    };

    const handleDobLastNameChange = (value: string) => {
      // Only allow alphabetic characters and limit to 3 characters
      const alphabeticOnly = value.replace(/[^a-zA-Z]/g, "");
      const limitedValue = alphabeticOnly.slice(0, 3);

      setLastNameValue(limitedValue);

      // Trigger search when last name changes and we have DOB
      if (dobValue) {
        triggerDobSearch(limitedValue, dobValue);
      }

      // Auto-focus date picker after 3 characters ONLY if DOB is empty
      if (limitedValue.length === 3 && !dobValue) {
        setShouldFocusDatePicker(true);
        setTimeout(() => {
          setShouldFocusDatePicker(false);
        }, 100);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isFocused || activeSuggestions.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < activeSuggestions.length - 1 ? prev + 1 : prev
          );
          break;

        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;

        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < activeSuggestions.length) {
            const selected = activeSuggestions[selectedIndex];
            if (injectedSuggestions && injectedSuggestions.length > 0) {
              // Handle injected suggestions (string type)
              setSearchValue(selected as string);
              setIsFocused(false);
              setSelectedIndex(-1);
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onSuggestionSelect?.(selected as any);
            } else {
              // Handle patient suggestions
              handleSuggestionClick(selected as Patient);
            }
          }
          break;

        case "Escape":
          e.preventDefault();
          setIsFocused(false);
          setSelectedIndex(-1);
          break;
      }
    };

    const handleToggleChange = (value: string) => {
      if (!value || value === activeToggle) {
        return;
      }
      setActiveToggle(value);
      setSearchValue("");
      setDobValue(undefined);
      setLastNameValue("");
      setValidationError(null);
      setDobError(null);
      setSelectedIndex(-1);
      // Immediately hide suggestions and clear local results to avoid
      // showing previous results when switching toggles.
      setIsFocused(false);
      setIsDatePickerOpen(false);
      // Cancel any pending timers that might still trigger a search
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        setDebounceTimer(null);
      }
      if (validationTimer) {
        clearTimeout(validationTimer);
        setValidationTimer(null);
      }
      clearLocalSearch();
      setLocalLoading(false);
      onToggleChange?.(value);

      // Auto-focus last name input when switching to dob-name
      if (value === "dob-name") {
        setTimeout(() => {
          lastNameInputRef.current?.focus();
        }, 100);
      }

      // Auto-focus medicaid input when switching to medicaid-id
      if (value === "medicaid-id") {
        setTimeout(() => {
          medicaidInputRef.current?.focus();
        }, 100);
      }
    };

    const handleSuggestionClick = (patient: Patient) => {
      dispatch(setSelectedPatient(patient));
      if (patient?.memberId) {
        navigate(`/dashboard/${patient.memberId}`);
      }
      onSuggestionSelect?.(patient);
      setIsFocused(false);
      setSearchValue("");
      setDobValue(undefined);
      setLastNameValue("");
      setValidationError(null);
      setSelectedIndex(-1);
      clearLocalSearch();

      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };

    const shouldShowSuggestions =
      (isFocused || isDatePickerOpen) &&
      !validationError &&
      !dobError &&
      !error &&
      ((injectedSuggestions && injectedSuggestions.length > 0) ||
        localResults.length > 0 ||
        localLoading ||
        localSearchError ||
        (!!localLastMessage && localResults.length === 0));

    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        // If date picker popover is open, allow interactions without closing
        if (isDatePickerOpen) return;
        const target = event.target as Node;
        if (containerRef.current && !containerRef.current.contains(target)) {
          setIsFocused(false);
          setIsDatePickerOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [isDatePickerOpen]);

    const renderSuggestionsDropdown = () => {
      if (!shouldShowSuggestions) {
        return null;
      }

      return (
        <div
          className='absolute top-full left-0 right-0 mt-1 max-h-[228px] w-full overflow-y-auto rounded-md border border-border bg-white dark:bg-blue-800 p-2 shadow-md'
          style={{ zIndex: 0 }}
          onMouseLeave={() => setSelectedIndex(-1)}>
          {(() => {
            const hasInjectedSuggestions =
              !!injectedSuggestions && injectedSuggestions.length > 0;
            if (localLoading && !hasInjectedSuggestions) {
              return (
                <div className='px-3 py-2 text-sm text-grey-500'>Searching...</div>
              );
            }

            if (localSearchError) {
              return (
                <div className='px-3 py-2 text-sm text-red-500 dark:text-red-400'>
                  {localSearchError.message ||
                    localSearchError.error ||
                    "Unable to complete search. Please try again."}
                </div>
              );
            }

            if (hasInjectedSuggestions) {
              const suggestionOccurrences = new Map<string, number>();
              return injectedSuggestions.map((text, idx) => {
                const currentOccurrence = suggestionOccurrences.get(text) ?? 0;
                suggestionOccurrences.set(text, currentOccurrence + 1);
                const suggestionKey = `${text}-${currentOccurrence}`;
                return (
                  <div key={suggestionKey} className='cursor-pointer'>
                <Typography
                  variant='medium-14'
                  className={cn(
                    "cursor-pointer px-3 py-2 hover:bg-grey-200 dark:hover:bg-grey-700 hover:text-accent-foreground flex gap-2",
                    selectedIndex === idx && "bg-grey-200 dark:bg-grey-700"
                  )}
                  onMouseDown={() => {
                    setSearchValue(text);
                    setIsFocused(false);
                    setSelectedIndex(-1);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onSuggestionSelect?.(text as any);
                  }}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  onMouseLeave={() => setSelectedIndex(-1)}>
                  {text}
                </Typography>
                {idx < injectedSuggestions.length - 1 && (
                  <div className='border-t border-grey-300 dark:border-grey-700'></div>
                )}
              </div>
                );
              });
            }

            if (localResults.length > 0) {
              return localResults.map((patient, idx) => (
                <div key={patient.memberId} className='cursor-pointer'>
                <Typography
                  variant='medium-14'
                  className={cn(
                    "cursor-pointer px-3 py-2 hover:bg-grey-200 dark:hover:bg-grey-700 hover:text-accent-foreground flex gap-2",
                    selectedIndex === idx && "bg-grey-200 dark:bg-grey-700"
                  )}
                  onMouseDown={() => handleSuggestionClick(patient)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  onMouseLeave={() => setSelectedIndex(-1)}>
                  {patient.medicadeId}
                  <Typography variant='medium-14' className='text-grey-300'>
                    |
                  </Typography>
                  {patient.memberFirstName + " " + patient.memberLastName}
                  <Typography variant='medium-14' className='text-grey-300'>
                    |
                  </Typography>
                  {formatDateWithoutTimezone(patient.memberDob)}
                </Typography>
                {idx < localResults.length - 1 && (
                  <div className='border-t border-grey-300 dark:border-grey-700'></div>
                )}
              </div>
              ));
            }

            return (
              <div className='px-3 py-2 text-sm text-grey-500'>
                {localLastMessage || "No patients found"}
              </div>
            );
          })()}
        </div>
      );
    };

    const renderErrorMessages = () => {
      // Only show search input related errors here (not dobError)
      // In dob-name mode, validationError is shown below the last name input, so don't show it here
      const hasAnyError = activeToggle === "medicaid-id" ? hasError : error;

      if (!hasAnyError) {
        return null;
      }

      return (
        <div className='mt-1 px-2 md:absolute md:left-0 md:right-0 md:mt-1 md:z-10 md:pointer-events-none'>
          <div className='md:mx-0 md:px-2'>
            {activeToggle === "medicaid-id" && validationError && (
              <div className='text-xs text-red-500 md:pointer-events-auto'>
                {validationError}
              </div>
            )}

            {error && (
              <div className='text-xs text-red-500 md:pointer-events-auto'>
                {error}
              </div>
            )}
          </div>
        </div>
      );
    };

    React.useEffect(() => {
      return () => {
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }
        if (validationTimer) {
          clearTimeout(validationTimer);
        }
      };
    }, [debounceTimer, validationTimer]);

    // Clear local search error when search value changes
    React.useEffect(() => {
      if (localSearchError) {
        setLocalSearchError(null);
      }
    }, [searchValue]);

    // Reset selected index when suggestions change
    React.useEffect(() => {
      setSelectedIndex(-1);
    }, [localResults, injectedSuggestions]);

    // Clear search state when navigation happens (route changes to patient route)
    React.useEffect(() => {
      const pathname = location.pathname;
      const prevPathname = prevPathnameRef.current;

      // Check if navigating to a patient route (e.g., /dashboard/{memberId})
      const patientRoutePattern = /^\/dashboard\/[^/]+$/;
      const isPatientRoute = patientRoutePattern.test(pathname);

      // Clear search state when navigating to a patient route (route has changed)
      if (isPatientRoute && pathname !== prevPathname) {
        clearAllSearchState();
      }

      // Update the previous pathname
      prevPathnameRef.current = pathname;
    }, [location.pathname, clearAllSearchState]);
    return (
      <div
        ref={containerRef}
        className='flex flex-col md:flex-row w-full gap-3 md:gap-4 md:items-start'>
        {/* search input for medicaid id */}
        {activeToggle === "medicaid-id" && (
          <div className='relative w-full md:w-[57%] min-h-[48px]'>
            <div
              className={cn(
                "flex w-full h-12 md:h-[48px] overflow-hidden items-center gap-3 rounded-md border bg-grey-100 text-base",
                "border-grey-400 dark:bg-blue-800 dark:border-grey-700",
                "focus-within:ring-0 focus-within:ring-offset-0",
                error ? "border-red-500" : ""
              )}
              style={{ padding: "10px 6px 10px 12px" }}>
              {icon || (
                <>
                  <img
                    src={searchIcon}
                    alt='searchicon'
                    className='h-4 w-4 block dark:hidden'
                  />
                  <img
                    src={searchIconLight}
                    alt='searchicon'
                    className='h-4 w-4 hidden dark:block'
                  />
                </>
              )}
              <input
                ref={medicaidInputRef}
                type='text'
                value={searchValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setTimeout(() => setIsFocused(false), 200)}
                placeholder={placeholder}
                autoComplete='off'
                className='flex-1 bg-grey-100 dark:bg-blue-800 text-[14px] leading-[22px] font-normal font-inter outline-none ring-0 focus:ring-0 focus:outline-none placeholder:text-grey-600'
                style={{
                  fontStyle: "normal",
                  letterSpacing: "0px",
                }}
              />
            </div>

            {renderSuggestionsDropdown()}

            {renderErrorMessages()}
          </div>
        )}

        {/* search input for dob+lastname */}
        {activeToggle === "dob-name" && (
          <div className='relative w-full md:w-[57%] min-h-[48px]'>
            <div className='flex flex-col sm:flex-row gap-3'>
              <div className='w-full sm:w-1/2 min-w-0 flex flex-col'>
                <div
                  className={cn(
                    "flex w-full h-12 md:h-[48px] items-center gap-3 rounded-md border bg-grey-100 text-base",
                    "border-grey-400 dark:bg-blue-800 dark:border-grey-700",
                    "focus-within:ring-0 focus-within:ring-offset-0",
                    // Show red border only for last-name validation errors, not DOB errors
                    validationError && !dobError ? "border-red-500" : ""
                  )}
                  style={{ padding: "10px 6px 10px 12px", minWidth: 0 }}>
                  {icon || (
                    <>
                      <img
                        src={searchIcon}
                        alt='searchicon'
                        className='h-4 w-4 block dark:hidden'
                      />
                      <img
                        src={searchIconLight}
                        alt='searchicon'
                        className='h-4 w-4 hidden dark:block'
                      />
                    </>
                  )}
                  <div className='flex-1 min-w-0'>
                    <input
                      ref={lastNameInputRef}
                      type='text'
                      value={lastNameValue}
                      onChange={(e) => handleDobLastNameChange(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onFocus={() => {
                        setIsFocused(true);
                        setIsDatePickerOpen(false);
                      }}
                      onBlur={() => setTimeout(() => setIsFocused(false), 200)}
                      placeholder='Enter First 3 Letters of Last Name'
                      maxLength={3}
                      className='w-full bg-transparent text-[14px] leading-[22px] font-normal font-inter outline-none ring-0 focus:ring-0 focus:outline-none placeholder:text-grey-600'
                      style={{
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      }}
                    />
                  </div>
                </div>
                {/* Search input errors (validationError) - show below last name input */}
                {validationError &&
                  activeToggle === "dob-name" &&
                  !dobError && (
                    <div className='mt-1 px-2 md:absolute md:left-0 md:right-0 md:mt-12 md:z-10 md:pointer-events-none'>
                      <div className='text-xs text-red-500'>
                        {validationError}
                      </div>
                    </div>
                  )}
              </div>
              <div className='min-h-[48px] w-full sm:w-1/2 flex flex-col md:relative'>
                <div className='relative'>
                  <DatePicker
                    value={dobValue}
                    onChange={(date: Date | undefined) => {
                      // KEY FIX: Clear dobValue immediately when date becomes invalid/incomplete
                      // This prevents the DatePicker from re-rendering with the old complete date
                      if (date === undefined) {
                        // User explicitly cleared the field
                        handleDobDateChange();
                      } else if (date && !isNaN(date.getTime())) {
                        // Valid complete date - update normally
                        handleDobDateChange(date);
                      } else {
                        // Invalid or incomplete date - clear the dobValue state
                        setDobValue(undefined);
                        // Don't trigger search with invalid date
                      }
                    }}
                    autoFocus={shouldFocusDatePicker}
                    error={dobError}
                    onValidationError={(error) => {
                      setDobError(error);
                      // Ensure last-name validation error is cleared when DOB has an error
                      setValidationError(null);

                      // Cancel pending timers to prevent scheduled validation/search
                      if (debounceTimer) {
                        clearTimeout(debounceTimer);
                        setDebounceTimer(null);
                      }
                      if (validationTimer) {
                        clearTimeout(validationTimer);
                        setValidationTimer(null);
                      }

                      // Close suggestions dropdown and clear results when DOB is invalid
                      setIsFocused(false);
                      // prevent the DatePicker's onOpenChange close handler from re-focusing
                      suppressFocusOnCloseRef.current = true;
                      setIsDatePickerOpen(false);
                      clearLocalSearch();
                      setLocalLoading(false);
                      setSelectedIndex(-1);
                    }}
                    className='placeholder:text-grey-600'
                    preserveInputOnEmpty={false}
                    onInputFocus={() => {
                      setIsFocused(true);
                      // If both DOB and last-name are present (last-name 3 chars), trigger search
                    }}
                    onInputKeyDown={handleKeyDown}
                    onOpenChange={(open: boolean) => {
                      setIsDatePickerOpen(open);
                      if (open) {
                        setIsFocused(true);
                        // When popover opens, also trigger search if inputs are ready
                      } else {
                        setTimeout(() => {
                          setIsFocused(true);
                        }, 50);
                      }
                    }}
                  />
                </div>
                {/* Date picker errors (dobError) - show below date picker */}
                {dobError && (
                  <div className='mt-1 px-2 md:absolute md:left-0 md:right-0 md:mt-12 md:z-10 md:pointer-events-none'>
                    <div
                      className='text-xs text-red-500 md:pointer-events-auto'
                      style={{ width: "max-content" }}>
                      {dobError}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {renderSuggestionsDropdown()}

            {/* Only show general error prop here, not validationError or dobError */}
            {error && (
              <div className='mt-1 px-2 md:absolute md:left-0 md:right-0 md:mt-12 md:z-10 md:pointer-events-none'>
                <div
                  className='text-xs text-red-500'
                  style={{ width: "max-content" }}>
                  {error}
                </div>
              </div>
            )}
          </div>
        )}

        <div className='flex items-start justify-center md:justify-start w-full md:max-w-[40%] min-w-[290px] min-h-[48px]'>
          <ToggleGroup
            type='single'
            value={activeToggle}
            onValueChange={handleToggleChange}
            className={cn(
              "gap-0 rounded-[6px] overflow-hidden h-12 md:h-[48px] w-max-content",
              "bg-grey-300 dark:bg-toggle-inactive-bg",
              "dark:border dark:border-grey-700"
            )}
            style={{
              borderRadius: "6px",
              padding: "6px",
              display: "flex",
              gap: "12px",
            }}>
            {toggleOptions.map((option) => (
              <ToggleGroupItem
                key={option.value}
                value={option.value}
                onClick={(e) => {
                  if (activeToggle === option.value) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                className={cn(
                  "px-3 lg:px-4 py-2 text-sm font-medium transition-all border-0 outline-none ring-0 rounded-md",
                  "focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
                  "flex items-center gap-1.5 h-full",
                  activeToggle === option.value
                    ? "bg-primary-700 text-white hover:bg-primary-700 hover:text-white cursor-default"
                    : "bg-grey-100 text-grey-700 hover:bg-white cursor-pointer",
                  activeToggle === option.value
                    ? "dark:bg-toggle-active-bg dark:text-white dark:hover:bg-toggle-active-bg dark:hover:text-white"
                    : "dark:bg-[#32414A] dark:text-white cursor-pointer"
                )}
                style={{
                  height: "100%",
                  boxShadow: "none",
                  transition:
                    "box-shadow 0.25s ease, background-color 0.25s ease",
                }}
                onMouseEnter={(e) => {
                  if (activeToggle === option.value) {
                    e.currentTarget.style.boxShadow = "none";
                  } else {
                    const isDark =
                      document.documentElement.classList.contains("dark");
                    e.currentTarget.style.boxShadow = isDark
                      ? "0px 2px 8px rgba(255, 255, 255, 0.08)"
                      : "0px 2px 6px rgba(0,0,0,0.15)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                }}>
                {activeToggle === option.value && (
                  <div
                    className='w-1.5 h-1.5 rounded-full bg-white'
                    style={{ boxShadow: "none" }}
                  />
                )}
                {option.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>
    );
  }
);

SearchWithToggle.displayName = "SearchWithToggle";

export default SearchWithToggle;
